import React, { useState } from 'react';
import { ShieldCheckIcon, ArrowLeftIcon, CubeTransparentIcon, BoltIcon } from '@heroicons/react/24/outline';
import { SuiteDashboard } from './SuiteDashboard';
import type { Page, NavParams, UserProfile, Organization, CountryRiskData, GeopoliticalEvent, Student, EnrolledCourse, LessonPlan, ActiveFramework, FrameworkComponent, AnalysisOrPlanResult } from '@types';

interface ResiliencyHubProps {
    currentUser: UserProfile | null;
    currentOrganization: Organization | null;
    onBack: () => void;
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
    riskData?: CountryRiskData[];
    alerts?: GeopoliticalEvent[];
    navParams?: NavParams;
    onRequestHelp: (suiteName: string, workflowName?: string) => void;
    onOpenChatBot?: () => void;
    onNavigateToGtgDashboard: () => void;
    onNavigateToGsocDashboard: () => void;
    onNavigateToSuite: (suiteName: string) => void;
    onOpenAvToolsModal: () => void;
    students?: Student[];
    onCreateStudent?: (studentData: any) => Promise<Student>;
    onCreateCustomCourse?: (course: EnrolledCourse, studentId: string) => void;
    onUpdateStudentCourse?: (studentId: string, courseId: string, updates: Partial<EnrolledCourse>) => void;
    customLmsModules?: any[];
    onCreateCustomModule?: (newModule: any) => void;
    customCourseDefinitions?: any[];
    onCreateCourseDefinition?: (courseDefinition: { courseName: string; modules: string[] }) => void;
    onEnrollStudent?: (course: EnrolledCourse, studentId: string) => void;
    standardCourseDefinitions?: any[];
    lessonPlans?: LessonPlan[];
    onSaveLessonPlan?: (moduleId: string, title: string, content: string) => void;
    onUpdateSrmProjectModule?: (projectId: string, moduleId: string, resultData: AnalysisOrPlanResult) => Promise<void>;
    activeFramework?: ActiveFramework | null;
    onUpdateFrameworkComponent?: (componentId: string, updates: Partial<FrameworkComponent>) => Promise<void>;
}

type HubTab = 'srm' | 'crisis';

const TAB_CONFIG: { id: HubTab; label: string; sublabel: string; activeColor: string; dotColor: string }[] = [
    { id: 'srm',    label: 'Risk Architecture',   sublabel: 'Proactive Controls',  activeColor: 'text-blue-600',    dotColor: 'bg-blue-500' },
    { id: 'crisis', label: 'Crisis & Continuity',  sublabel: 'Response & Recovery', activeColor: 'text-amber-600',   dotColor: 'bg-amber-500' },
];

export const ResiliencyHub: React.FC<ResiliencyHubProps> = (props) => {
    const [activeTab, setActiveTab] = useState<HubTab>(() => {
        const suiteName = props.navParams?.suiteName;
        const suiteId   = props.navParams?.suiteId;
        if (suiteName === 'Company Business Resiliency and Crises Management Program' || suiteId === 'cmp' || suiteId === 'crisis') return 'crisis';
        if (suiteName === 'HSSE Compliance' || suiteId === 'hsse') return 'crisis';
        return 'srm';
    });

    const srmSuiteName    = 'Security Risk Management';
    const crisisSuiteName = 'Company Business Resiliency and Crises Management Program';
    const TabIcon: React.FC<{ tab: HubTab }> = ({ tab }) => {
        if (tab === 'srm')    return <ShieldCheckIcon className="w-4 h-4" />;
        if (tab === 'crisis') return <BoltIcon className="w-4 h-4" />;
        return <BoltIcon className="w-4 h-4" />;
    };

    return (
        <div className="w-full max-w-[2000px] mx-auto space-y-6 animate-fade-in pb-12">

            {/* HUB HEADER */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
                <div className="flex items-center gap-4">
                    <button
                        onClick={props.onBack}
                        className="p-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all shadow-sm group"
                    >
                        <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Enterprise Resiliency Hub</h1>
                        <p className="text-slate-500 text-sm font-medium mt-0.5 flex items-center gap-2">
                            <CubeTransparentIcon className="w-4 h-4" /> Consolidated Risk, Response & Compliance Workspace
                        </p>
                    </div>
                </div>

                {/* 3-TAB SWITCHER */}
                <div className="flex p-1 bg-slate-100 rounded-2xl border border-slate-200 shadow-inner gap-0.5">
                    {TAB_CONFIG.map(tab => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
                                    isActive
                                        ? `bg-white ${tab.activeColor} shadow-md ring-1 ring-slate-200`
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                                }`}
                            >
                                <TabIcon tab={tab.id} />
                                <span className="hidden sm:inline">{tab.label}</span>
                                {isActive && <span className={`w-1.5 h-1.5 rounded-full ${tab.dotColor} hidden md:block`} />}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* TAB CONTEXT BANNER */}
            {activeTab === 'srm' && (
                <div className="flex items-start gap-3 px-4 py-3 bg-blue-50/80 border border-blue-200 rounded-2xl animate-fade-in">
                    <ShieldCheckIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-blue-900">Risk Architecture Mode</p>
                        <p className="text-xs text-blue-700 mt-0.5">Build proactive security controls and vulnerability blueprints before incidents occur. Use FSAT to baseline facilities, develop protection plans, and run threat analysis.</p>
                    </div>
                    <button
                        onClick={() => setActiveTab('crisis')}
                        className="flex-shrink-0 text-xs font-semibold text-blue-600 bg-blue-100 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                    >
                        Go to Crisis Plans →
                    </button>
                </div>
            )}
            {activeTab === 'crisis' && (
                <div className="flex items-start gap-3 px-4 py-3 bg-amber-50/80 border border-amber-200 rounded-2xl animate-fade-in">
                    <BoltIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-900">Crisis & Continuity Mode</p>
                        <p className="text-xs text-amber-700 mt-0.5">Activate response frameworks during live incident windows. Ensure your proactive controls (FSAT, Protection Plans) are current before activating a crisis plan.</p>
                    </div>
                    <button
                        onClick={() => setActiveTab('srm')}
                        className="flex-shrink-0 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                    >
                        ← Check Risk Controls
                    </button>
                </div>
            )}
            {/* MAIN CONTENT AREA — no wrapper frame so cards sit flush on the page */}
            <div>
                {activeTab === 'srm' && (
                    <div className="animate-fade-in-up">
                        <SuiteDashboard
                            {...props}
                            suiteName={srmSuiteName}
                            onBack={props.onBack}
                            onNavigate={(nav) => props.onNavigate({
                                ...nav,
                                page: nav.page === 'suiteDashboard' ? 'resiliencyHub' : nav.page,
                                params: { ...nav.params, suiteName: srmSuiteName }
                            })}
                        />
                    </div>
                )}
                {activeTab === 'crisis' && (
                    <div className="animate-fade-in-up">
                        <SuiteDashboard
                            {...props}
                            suiteName={crisisSuiteName}
                            onBack={props.onBack}
                            onNavigate={(nav) => props.onNavigate({
                                ...nav,
                                page: nav.page === 'suiteDashboard' ? 'resiliencyHub' : nav.page,
                                params: { ...nav.params, suiteName: crisisSuiteName }
                            })}
                        />
                    </div>
                )}
            </div>

            {/* HUB FOOTER — status cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {TAB_CONFIG.map(tab => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`text-left p-4 rounded-2xl border transition-all flex items-start gap-3 ${
                                isActive
                                    ? 'bg-white border-slate-300 shadow-md'
                                    : 'bg-slate-50/50 border-slate-200 hover:bg-white hover:shadow-sm'
                            }`}
                        >
                            <div className={`p-2 rounded-lg ${
                                tab.id === 'srm'    ? 'bg-blue-100 text-blue-600' :
                                tab.id === 'crisis' ? 'bg-amber-100 text-amber-600' :
                                'bg-orange-100 text-orange-600'
                            }`}>
                                <TabIcon tab={tab.id} />
                            </div>
                            <div>
                                <p className={`text-sm font-bold ${isActive ? tab.activeColor : 'text-slate-700'}`}>{tab.label}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{tab.sublabel}</p>
                            </div>
                            {isActive && <span className={`ml-auto text-xs font-semibold ${tab.dotColor.replace('bg-', 'text-')} bg-opacity-10`}>Active</span>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
